<template>
  <div class="relative">
    <!-- Bouton du menu utilisateur -->
    <button
      @click="isOpen = !isOpen"
      class="flex items-center space-x-3 text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
    >
      <div class="relative">
        <div class="h-8 w-8 rounded-full overflow-hidden bg-indigo-600 flex items-center justify-center ring-2 ring-white">
          <img v-if="userAvatar" :src="userAvatar" alt="Avatar" class="h-8 w-8 object-cover" />
          <span v-else class="text-sm font-medium text-white">{{ userInitials }}</span>
        </div>
        <!-- Badge organisation si logo disponible -->
        <div 
          v-if="organizationLogo" 
          class="absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full bg-white ring-2 ring-white overflow-hidden"
        >
          <img 
            :src="organizationLogoUrl" 
            :alt="organizationName || 'Logo'" 
            class="h-full w-full object-cover"
          />
        </div>
      </div>
      <div class="hidden md:block text-left min-w-0 flex-1">
        <p class="text-sm font-medium text-gray-700 truncate">{{ user?.nom }} {{ user?.prenom }}</p>
        <p class="text-xs text-gray-500 truncate">{{ user?.email }}</p>
      </div>
      <svg
        class="h-5 w-5 text-gray-400 flex-shrink-0"
        :class="{ 'transform rotate-180': isOpen }"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
      </svg>
    </button>

    <!-- Menu déroulant -->
    <div
      v-if="isOpen"
      class="absolute right-0 mt-2 w-64 rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50 overflow-hidden"
    >
      <div class="py-1" role="menu">
        <!-- Section Organisation -->
        <div v-if="organizationName" class="px-4 py-3 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
          <div class="flex items-center space-x-2.5">
            <div 
              v-if="organizationLogo" 
              class="h-8 w-8 rounded-md overflow-hidden bg-white border border-gray-200 flex-shrink-0 flex items-center justify-center shadow-sm"
            >
              <img 
                :src="organizationLogoUrl" 
                :alt="organizationName" 
                class="h-full w-full object-cover"
                @error="(e) => { (e.target as HTMLImageElement).style.display = 'none' }"
              />
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-0.5">Organisation</p>
              <p class="text-sm font-semibold text-gray-900 truncate">{{ organizationName }}</p>
            </div>
          </div>
        </div>

        <!-- Informations utilisateur -->
        <div class="px-4 py-3 border-b border-gray-100">
          <div class="flex items-center space-x-3">
            <div class="h-10 w-10 rounded-full overflow-hidden bg-indigo-600 flex items-center justify-center flex-shrink-0 ring-2 ring-gray-100">
              <img v-if="userAvatar" :src="userAvatar" alt="Avatar" class="h-10 w-10 object-cover" />
              <span v-else class="text-sm font-medium text-white">{{ userInitials }}</span>
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-sm font-semibold text-gray-900 truncate">{{ user?.nom }} {{ user?.prenom }}</p>
              <p class="text-xs text-gray-500 truncate mt-0.5">{{ user?.email }}</p>
              <p class="text-xs text-gray-400 mt-1.5 flex items-center gap-1.5">
                <span class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                  {{ getRoleLabel(user?.role) }}
                </span>
                <span class="text-gray-300">•</span>
                <span>{{ getUserTypeLabel(user?.userType) }}</span>
              </p>
            </div>
          </div>
        </div>

        <!-- Lien vers le profil -->
        <button
          @click="goToProfile"
          class="block w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150"
          role="menuitem"
        >
          <div class="flex items-center">
            <svg class="mr-3 h-4 w-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <span class="font-medium">Mon profil</span>
          </div>
        </button>

        <!-- Changer le mot de passe -->
        <button
          @click="showChangePassword = true"
          class="block w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150"
          role="menuitem"
        >
          <div class="flex items-center">
            <svg class="mr-3 h-4 w-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
            <span class="font-medium">Changer le mot de passe</span>
          </div>
        </button>

        <!-- Séparateur -->
        <div class="border-t border-gray-100 my-1"></div>

        <!-- Déconnexion -->
        <button
          @click="handleLogout"
          class="block w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors duration-150"
          role="menuitem"
        >
          <div class="flex items-center">
            <svg class="mr-3 h-4 w-4 text-red-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            <span class="font-medium">Se déconnecter</span>
          </div>
        </button>
      </div>
    </div>

    <!-- Modal de changement de mot de passe -->
    <Modal v-model="showChangePassword" title="Changer le mot de passe">
      <form @submit.prevent="changePassword" class="space-y-4">
        <div>
          <label for="currentPassword" class="block text-sm font-medium text-gray-700">
            Mot de passe actuel
          </label>
          <input
            id="currentPassword"
            v-model="passwordForm.currentPassword"
            type="password"
            required
            class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          />
        </div>

        <div>
          <label for="newPassword" class="block text-sm font-medium text-gray-700">
            Nouveau mot de passe
          </label>
          <input
            id="newPassword"
            v-model="passwordForm.newPassword"
            type="password"
            required
            class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          />
        </div>

        <div>
          <label for="confirmPassword" class="block text-sm font-medium text-gray-700">
            Confirmer le nouveau mot de passe
          </label>
          <input
            id="confirmPassword"
            v-model="passwordForm.confirmPassword"
            type="password"
            required
            class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          />
        </div>

        <div class="flex justify-end space-x-3">
          <button
            type="button"
            @click="showChangePassword = false"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Annuler
          </button>
          <button
            type="submit"
            :disabled="loading || !isPasswordFormValid"
            class="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 disabled:opacity-50"
          >
            {{ loading ? 'Changement...' : 'Changer le mot de passe' }}
          </button>
        </div>
      </form>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth.store'
import { useOrganizationsStore } from '@/stores/organizations.store'
import Modal from './Modal.vue'

const router = useRouter()
const authStore = useAuthStore()
const organizationsStore = useOrganizationsStore()

// État local
const isOpen = ref(false)
const showChangePassword = ref(false)
const loading = ref(false)

// Formulaire de changement de mot de passe
const passwordForm = reactive({
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
})

// Computed properties
const user = computed(() => authStore.user)
const userAvatar = computed(() => user.value?.avatar || '')

const userInitials = computed(() => {
  if (!user.value) return 'U'
  return `${user.value.nom?.charAt(0) || ''}${user.value.prenom?.charAt(0) || ''}`.toUpperCase()
})

// Récupérer l'organisation de l'utilisateur
const userOrganization = computed(() => {
  if (!user.value?.organizationId) return null
  
  // Si organizationId est un objet (populate), retourner directement
  if (typeof user.value.organizationId === 'object' && user.value.organizationId !== null) {
    return user.value.organizationId
  }
  
  // Sinon, chercher dans le store des organisations
  const orgId = typeof user.value.organizationId === 'string' 
    ? user.value.organizationId 
    : user.value.organizationId._id || user.value.organizationId
    
  return organizationsStore.getOrganizationById(orgId) || null
})

const organizationName = computed(() => {
  if (!userOrganization.value) return null
  return userOrganization.value.name || null
})

const organizationLogo = computed(() => {
  if (!userOrganization.value) return null
  return userOrganization.value.logo || null
})

const organizationLogoUrl = computed(() => {
  if (!organizationLogo.value) return ''
  // Si le logo commence par /uploads, c'est un chemin relatif, on ajoute l'URL de l'API
  if (organizationLogo.value.startsWith('/uploads')) {
    // Utiliser la même URL de base que dans api.ts mais sans /api/v1 pour les fichiers statiques
    const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001'
    return `${apiBaseUrl}${organizationLogo.value}`
  }
  // Sinon, c'est déjà une URL complète
  return organizationLogo.value
})

const isPasswordFormValid = computed(() => {
  return passwordForm.currentPassword && 
         passwordForm.newPassword && 
         passwordForm.confirmPassword &&
         passwordForm.newPassword === passwordForm.confirmPassword &&
         passwordForm.newPassword.length >= 6
})

// Méthodes
const getRoleLabel = (role: string) => {
  const roleLabels: Record<string, string> = {
    'SUPER_ADMIN': 'Super Administrateur',
    'ADMIN': 'Administrateur',
    'MANAGER': 'Manager',
    'EMPLOYEE': 'Employé',
    'VIEWER': 'Lecteur'
  }
  return roleLabels[role] || role
}

const getUserTypeLabel = (userType: string) => {
  const typeLabels: Record<string, string> = {
    'INTERNAL': 'Interne',
    'EXTERNAL': 'Externe'
  }
  return typeLabels[userType] || userType
}

const goToProfile = () => {
  isOpen.value = false
  console.log('Aller au profil')
}

const handleLogout = async () => {
  try {
    await authStore.logout()
    router.push('/login')
  } catch (error) {
    console.error('Erreur lors de la déconnexion:', error)
  }
}

const changePassword = async () => {
  if (!isPasswordFormValid.value) return

  try {
    loading.value = true
    await authStore.changePassword(passwordForm.currentPassword, passwordForm.newPassword)
    
    // Réinitialiser le formulaire
    passwordForm.currentPassword = ''
    passwordForm.newPassword = ''
    passwordForm.confirmPassword = ''
    showChangePassword.value = false
    
    // Afficher un message de succès
    alert('Mot de passe changé avec succès')
  } catch (error: any) {
    alert(error.response?.data?.message || 'Erreur lors du changement de mot de passe')
  } finally {
    loading.value = false
  }
}

// Fermer le menu quand on clique à l'extérieur
const handleClickOutside = (event: Event) => {
  const target = event.target as Element
  if (!target.closest('.relative')) {
    isOpen.value = false
  }
}

onMounted(async () => {
  document.addEventListener('click', handleClickOutside)
  
  // Charger les organisations si elles ne sont pas déjà chargées
  if (organizationsStore.organizations.length === 0 && user.value?.organizationId) {
    try {
      await organizationsStore.fetchOrganizations()
    } catch (error) {
      console.error('Erreur lors du chargement des organisations:', error)
    }
  }
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>
